import React, { useEffect, useState } from "react";
import { CustomDialog, NormalSwitch } from "../../../components/StyledComponents";
import { DialogActions, DialogContent, DialogTitle } from "@mui/material";
import {
  ControlpanelPrimaryButton,
  ControlpanelSecondaryButton,
  GrayIconButton,
} from "../../../components/StyledButton";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";
import CustomInput from "../../../components/CustomInput";
import CustomSelect from "../../../components/CustomSelect";
import LeaveApi from "../../../axios/LeaveAndHoliday/LeaveApi";
import { useLoading } from "../../../context/LoadingContext";
import { toast } from "react-toastify";
import CustomTimePickerHaveSecondV2 from "../../../components/CustomTimePickerHaveSecondV2";

const FormTwoColRow = ({ label, children }) => (
  <div className="grid grid-cols-1 md:grid-cols-12 gap-2 gap-y-2 md:gap-y-0 items-center">
    <div className="md:col-span-6">{label}</div>
    <div className="md:col-span-6">{children}</div>
  </div>
);

// Chuẩn hóa hoursPerLeaveDay về "HH:mm:ss" (API có thể trả string hoặc object LocalTime)
const normalizeHoursPerLeaveDay = (value) => {
  if (!value) return "08:00:00";
  if (typeof value === "string") return value;
  if (typeof value === "object") {
    const hour = String(value.hour ?? 0).padStart(2, "0");
    const minute = String(value.minute ?? 0).padStart(2, "0");
    const second = String(value.second ?? 0).padStart(2, "0");
    return `${hour}:${minute}:${second}`;
  }
  return "08:00:00";
};

const mapDataToForm = (data) => ({
  leaveTypeId: data?.leaveTypeId ?? "",
  leaveTypeCode: data?.leaveTypeCode ?? "",
  remark: data?.remark ?? "",
  remarkEN: data?.remarkEN ?? "",
  entitlement: Number(data?.entitlement) || 1,
  maxLeaveDuration: Number(data?.maxLeaveDuration) || 5,
  isMonthlyLimit:
    data?.isMonthlyLimit !== undefined ? Boolean(data.isMonthlyLimit) : true,
  monthlyLimitDays: Number(data?.monthlyLimitDays) || 5,
  isWeeklyLimit:
    data?.isWeeklyLimit !== undefined ? Boolean(data.isWeeklyLimit) : true,
  weeklyLimitDays: Number(data?.weeklyLimitDays) || 5,
  hoursPerLeaveDay: normalizeHoursPerLeaveDay(data?.hoursPerLeaveDay),
  overflowLeaveTypeId: data?.overflowLeaveTypeId ?? "",
  isMonthlyAccrual: Boolean(data?.isMonthlyAccrual),
  monthlyAccrualDay: Number(data?.monthlyAccrualDay) || 1,
  isCarry: Boolean(data?.isCarry),
  maxCarry: Number(data?.maxCarry) || 0,
  carryExpireMonth: Number(data?.carryExpireMonth) || 1,
  description: data?.description ?? "",
});

const EditLeaveTypeDialog = ({
  open,
  handleClose,
  data,
  onRefresh,
  refreshData,
}) => {
  const { t } = useTranslation();
  const { editLeaveType, getLeaveTypeData } = LeaveApi();
  const [show, hide] = useLoading();

  const yearStartMonthData = [
    { value: 1, label: t("common.january") },
    { value: 2, label: t("common.february") },
    { value: 3, label: t("common.march") },
    { value: 4, label: t("common.april") },
    { value: 5, label: t("common.may") },
    { value: 6, label: t("common.june") },
    { value: 7, label: t("common.july") },
    { value: 8, label: t("common.august") },
    { value: 9, label: t("common.september") },
    { value: 10, label: t("common.october") },
    { value: 11, label: t("common.november") },
    { value: 12, label: t("common.december") },
  ];

  const [formData, setFormData] = useState(() => mapDataToForm(data));
  const [leaveTypeOptions, setLeaveTypeOptions] = useState([]);

  const [validationMessages, setValidationMessages] = useState({
    leaveTypeCode: "",
    remark: "",
    remarkEN: "",
    entitlement: "",
    description: "",
  });

  useEffect(() => {
    if (!open || !data) return;
    setFormData(mapDataToForm(data));
    setValidationMessages({
      leaveTypeCode: "",
      remark: "",
      remarkEN: "",
      entitlement: "",
      description: "",
    });

    const fetchLeaveTypes = async () => {
      try {
        const res = await getLeaveTypeData({
          langId: localStorage.getItem("i18nextLngId"),
          isDeleted: true,
        });
        if (
          res?.data?.message === "Successful" ||
          res?.data?.message === "Thành công"
        ) {
          // Không cho chọn chính loại phép đang sửa làm đích chuyển đổi
          const list = (res.data.data ?? []).filter(
            (lt) => Number(lt.leaveTypeId) !== Number(data.leaveTypeId)
          );
          setLeaveTypeOptions(list);

          // Giữ giá trị đã lưu nếu còn trong list; không thì chọn phần tử đầu
          setFormData((prev) => {
            const currentId = Number(prev.overflowLeaveTypeId);
            const exists = list.some(
              (lt) => Number(lt.leaveTypeId) === currentId
            );
            if (exists) return prev;
            return {
              ...prev,
              overflowLeaveTypeId:
                list.length > 0 ? list[0].leaveTypeId : "",
            };
          });
        }
      } catch (err) {
        console.error("Error fetching leave types:", err);
      }
    };

    fetchLeaveTypes();
  }, [open, data?.leaveTypeId]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (type === "checkbox") {
      setFormData((prev) => ({
        ...prev,
        [name]: checked,
      }));
      return;
    }

    if (
      [
        "entitlement",
        "maxLeaveDuration",
        "monthlyLimitDays",
        "weeklyLimitDays",
        "monthlyAccrualDay",
        "maxCarry",
      ].includes(name)
    ) {
      if (value === "") {
        setFormData((prev) => ({
          ...prev,
          [name]: "",
        }));
        return;
      }
      const num = Number(value);
      if (Number.isNaN(num)) return;
      setFormData((prev) => ({
        ...prev,
        [name]: num,
      }));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const buildPayload = () => ({
    leaveTypeId: formData.leaveTypeId,
    leaveTypeCode: formData.leaveTypeCode.trim(),
    remark: formData.remark.trim(),
    remarkEN: formData.remarkEN.trim(),
    entitlement: Number(formData.entitlement) || 0,
    isMonthlyAccrual: formData.isMonthlyAccrual,
    monthlyAccrualDay: formData.isMonthlyAccrual
      ? Number(formData.monthlyAccrualDay) || 0
      : 0,
    isCarry: formData.isCarry,
    maxCarry: formData.isCarry ? Number(formData.maxCarry) || 0 : 0,
    carryExpireMonth: formData.isCarry
      ? Number(formData.carryExpireMonth) || 0
      : 0,
    description: formData.description?.trim() || "",
    maxLeaveDuration: Number(formData.maxLeaveDuration) || 0,
    isMonthlyLimit: formData.isMonthlyLimit,
    monthlyLimitDays: formData.isMonthlyLimit
      ? Number(formData.monthlyLimitDays) || 0
      : 0,
    hoursPerLeaveDay: formData.hoursPerLeaveDay || "00:00:00",
    weeklyLimitDays: formData.isWeeklyLimit
      ? Number(formData.weeklyLimitDays) || 0
      : 0,
    isWeeklyLimit: formData.isWeeklyLimit,
    overflowLeaveTypeId: Number(formData.overflowLeaveTypeId) || 0,
    isApplyForOldLeave: formData.isApplyForOldLeave,
  });

  const generateLeaveTypeCode = () => {
    const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
    let code = "";
    for (let i = 0; i < 16; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }

    setFormData((p) => ({ ...p, leaveTypeCode: code }));
    setValidationMessages((p) => ({ ...p, leaveTypeCode: "" }));
  };

  const validate = () => {
    const errors = {
      leaveTypeCode: "",
      remark: "",
      remarkEN: "",
      entitlement: "",
      description: "",
    };
    if (!formData.remark.trim()) {
      errors.remark = t("validation.required");
    }
    if (!formData.remarkEN.trim()) {
      errors.remarkEN = t("validation.required");
    }
    if (!formData.entitlement || formData.entitlement <= 0) {
      errors.entitlement = t("validation.invalid");
    }

    if (!formData.description?.trim()) {
      errors.description = t("validation.required");
    }

    setValidationMessages(errors);
    return !Object.values(errors).some(Boolean);
  };

  const handleSave = async () => {
    if (!validate()) return;

    const payload = buildPayload();

    show();
    try {
      const res = await editLeaveType(payload);
      if (
        res.data.message === "Successful" ||
        res.data.message === "Thành công"
      ) {
        toast.success(t("common.edit-leavetype-successfully"));
        handleClose?.();
        onRefresh?.();
        refreshData?.();
      } else {
        toast.error(res.data.message);
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Error");
    } finally {
      hide();
    }
  };

  const limitNumberInput = (name, value, disabled) => (
    <input
      type="number"
      name={name}
      min={0}
      disabled={disabled}
      value={value ?? ""}
      onChange={handleInputChange}
      className="form-control h-10 w-full disabled:bg-[#F3F4F6] disabled:cursor-not-allowed"
    />
  );

  return (
    <CustomDialog
      maxWidth="md"
      open={open}
      onClose={handleClose}
      disableEnforceFocus
    >
      <DialogTitle>
        <div className="flex justify-between">
          <div className="flex items-center gap-4">
            <div className="w-4 h-8 bg-[#D9F99D] rounded" />
            <p className="text-xl font-semibold">
              {t("leave.edit-leave-type")}
            </p>
          </div>
          <GrayIconButton onClick={handleClose}>
            <CloseIcon />
          </GrayIconButton>
        </div>
      </DialogTitle>

      <DialogContent>
        <div className="grid grid-cols-1 gap-4 pt-4">
          {/* Mã loại nghỉ phép */}
          <div>
            <label className="block text-sm text-[#171717] mb-2">
              {t("leave.code")}{" "}
              <span className="text-[#DC2626]">*</span>
            </label>
            <div className="flex">
              <input
                type="text"
                name="leaveTypeCode"
                maxLength={16}
                value={formData.leaveTypeCode}
                onChange={handleInputChange}
                placeholder={t("leave.enter-code")}
                className={`form-control h-10 flex-1 rounded-r-none ${
                  validationMessages.leaveTypeCode ? "invalid" : ""
                }`}
              />
              <button
                type="button"
                onClick={generateLeaveTypeCode}
                className="h-10 px-4 text-sm text-white bg-[#3B82F6] rounded-r-md hover:bg-[#2563EB] shrink-0"
              >
                {t("button.generate")}
              </button>
            </div>
            {validationMessages.leaveTypeCode && (
              <p className="text-sm text-red-500 mt-1">
                {validationMessages.leaveTypeCode}
              </p>
            )}
          </div>

          {/* Tên tiếng Anh | Tên tiếng Việt */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <CustomInput
              required
              title={t("leave.remarkEN")}
              name="remarkEN"
              value={formData.remarkEN}
              placeholder={t("leave.enter-leave-type-name")}
              handleInputChange={handleInputChange}
              isError={Boolean(validationMessages.remarkEN)}
              message={validationMessages.remarkEN}
              className="w-full"
            />
            <CustomInput
              required
              title={t("leave.remarkVI")}
              name="remark"
              value={formData.remark}
              placeholder={t("leave.enter-leave-type-name")}
              handleInputChange={handleInputChange}
              isError={Boolean(validationMessages.remark)}
              message={validationMessages.remark}
              className="w-full"
            />
          </div>

          {/* Số ngày tối đa | Thời gian tối thiểu */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <CustomInput
              required
              title={t("leave.max-leave-days")}
              inputType="number"
              name="entitlement"
              minValue={1}
              value={formData.entitlement}
              handleInputChange={handleInputChange}
              isError={Boolean(validationMessages.entitlement)}
              message={validationMessages.entitlement}
              className="w-full"
            />
            <CustomInput
              title={t("leave.max-leave-duration")}
              inputType="number"
              name="maxLeaveDuration"
              minValue={0}
              value={formData.maxLeaveDuration}
              handleInputChange={handleInputChange}
              className="w-full"
            />
          </div>

          {/* Giới hạn tháng */}
          <FormTwoColRow
            label={
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  name="isMonthlyLimit"
                  checked={formData.isMonthlyLimit}
                  onChange={handleInputChange}
                  className="w-5 h-5 accent-[#3B82F6]"
                />
                <span className="text-sm text-[#171717]">
                  {t("leave.monthly-limit")}
                </span>
              </label>
            }
          >
            {limitNumberInput(
              "monthlyLimitDays",
              formData.monthlyLimitDays,
              !formData.isMonthlyLimit
            )}
          </FormTwoColRow>

          {/* Giới hạn tuần */}
          <FormTwoColRow
            label={
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  name="isWeeklyLimit"
                  checked={formData.isWeeklyLimit}
                  onChange={handleInputChange}
                  className="w-5 h-5 accent-[#3B82F6]"
                />
                <span className="text-sm text-[#171717]">
                  {t("leave.weekly-limit")}
                </span>
              </label>
            }
          >
            {limitNumberInput(
              "weeklyLimitDays",
              formData.weeklyLimitDays,
              !formData.isWeeklyLimit
            )}
          </FormTwoColRow>

          {/* Giờ / ngày nghỉ */}
          <FormTwoColRow
            label={
              <span className="text-sm text-[#171717]">
                {t("common.hoursperleaveday")}
              </span>
            }
          >
            <CustomTimePickerHaveSecondV2
              hoursLabel={false}
              name="hoursPerLeaveDay"
              value={formData.hoursPerLeaveDay}
              onTimeChange={(value) =>
                setFormData((prev) => ({
                  ...prev,
                  hoursPerLeaveDay: value,
                }))
              }
              className="w-full"
            />
          </FormTwoColRow>

          {/* Áp dụng cho nghỉ cũ */}
          <FormTwoColRow
            label={
              <span className="text-sm font-medium text-[#171717]">
                {t("leave.isApplyForOldLeave")}
              </span>
            }
          >
            <NormalSwitch
              checked={formData.isApplyForOldLeave}
              onChange={(e) =>
                setFormData((p) => ({
                  ...p,
                  isApplyForOldLeave: e.target.checked,
                }))
              }
            />
          </FormTwoColRow>

          {/* Chuyển đổi khi hết phép */}
          <FormTwoColRow
            label={
              <span className="text-sm text-[#171717]">
                {t("leave.convert-leave-type")}
              </span>
            }
          >
            <CustomSelect
              name="overflowLeaveTypeId"
              value={formData.overflowLeaveTypeId}
              onChange={handleInputChange}
              options={leaveTypeOptions}
              dataType="leaveType"
              className="w-full"
            />
          </FormTwoColRow>

          {/* Tích lũy hàng tháng */}
          <FormTwoColRow
            label={
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  name="isMonthlyAccrual"
                  checked={formData.isMonthlyAccrual}
                  onChange={handleInputChange}
                  className="w-5 h-5 accent-[#3B82F6]"
                />
                <span className="text-sm text-[#171717]">
                  {t("leave.accrue-monthly")}
                </span>
              </label>
            }
          >
            {limitNumberInput(
              "monthlyAccrualDay",
              formData.monthlyAccrualDay,
              !formData.isMonthlyAccrual
            )}
          </FormTwoColRow>

          {/* Cho phép chuyển sang */}
          <FormTwoColRow
            label={
              <span className="text-sm font-medium text-[#171717]">
                {t("leave.allow-carryover")}
              </span>
            }
          >
            <NormalSwitch
              checked={formData.isCarry}
              onChange={(e) =>
                setFormData((p) => ({
                  ...p,
                  isCarry: e.target.checked,
                }))
              }
            />
          </FormTwoColRow>

          {formData.isCarry && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <CustomInput
                title={t("leave.max-carryover-days")}
                inputType="number"
                name="maxCarry"
                minValue={0}
                value={formData.maxCarry}
                handleInputChange={handleInputChange}
                className="w-full"
              />
              <div>
                <label className="block text-sm text-[#171717] mb-2">
                  {t("leave.carryover-expire-month")}
                </label>
                <CustomSelect
                  name="carryExpireMonth"
                  value={formData.carryExpireMonth}
                  onChange={handleInputChange}
                  options={yearStartMonthData}
                  className="w-full"
                />
              </div>
            </div>
          )}

          {/* Mô tả */}
          <div>
            <label className="block text-sm text-[#171717] mb-2">
              {t("leave.leave-type-description")}
              <span className="text-[#DC2626]">*</span>
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder={t("leave.enter-description")}
              rows={3}
              className="form-control w-full resize-y min-h-[80px] p-2"
            />
            {validationMessages.description && (
              <p className="text-sm text-red-500 mt-1">
                {validationMessages.description}
              </p>
            )}
          </div>
        </div>
      </DialogContent>

      <DialogActions>
        <div className="flex gap-2 px-4 py-4">
          <ControlpanelSecondaryButton onClick={handleClose}>
            {t("button.cancel")}
          </ControlpanelSecondaryButton>
          <ControlpanelPrimaryButton
            onClick={handleSave}
            disabled={
              !formData.remark ||
              !formData.remarkEN ||
              !formData.entitlement ||
              !formData.description?.trim()
            }
          >
            {t("button.save")}
          </ControlpanelPrimaryButton>
        </div>
      </DialogActions>
    </CustomDialog>
  );
};

export default EditLeaveTypeDialog;
